import React, { useEffect, useRef, useState } from "react";
import { Heart } from "lucide-react";
import PowerChart from "./PowerChart";
import { averageHr, formatLapTime } from "../../utils/freeTraining";
import { useZoneFloors } from "../../contexts/ZoneFloorsContext";
import {
  ZONE_COLOURS,
  ZONE_LABELS,
  percentOfMax,
  zoneFloors,
  zoneScale,
} from "../../utils/heartRateZones";

// Free training's heart-rate view (#146, #157), laid out like a sports watch's
// heart-rate page: the number big and in your zone's colour, % of max beside
// it, a five-zone scale with a pointer, the last ten minutes on zone bands,
// then average and max. Power joins the figures and the chart once a machine
// is paired.
//
// It fills whatever box the Coach gives it, so it measures itself: a short box
// (a phone on its side) shrinks the number and folds the tiles into the hero
// row rather than overflowing.

const useBoxHeight = () => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const obs = new ResizeObserver(([entry]) => setHeight(Math.round(entry.contentRect.height)));
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return [ref, height];
};

const Tile = ({ label, children, colour }) => (
  <div className="min-w-0 rounded-xl border border-white/[0.06] bg-white/[0.03] px-2.5 py-1.5">
    <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">{label}</p>
    <p
      className="font-timer text-lg font-semibold leading-tight tabular-nums text-white"
      style={colour ? { color: colour } : undefined}
    >
      {children}
    </p>
  </div>
);

function ZoneScaleBar({ floors, max, bpm, zone }) {
  const { segments, at } = zoneScale(floors, max);
  const pointer = bpm != null ? at(bpm) : null;
  return (
    <div aria-hidden="true">
      <div className="relative h-5">
        {pointer != null && (
          <span
            className="absolute top-0 h-0 w-0 -translate-x-1/2 border-x-[6px] border-t-[7px] border-x-transparent border-t-white transition-[left] duration-500"
            style={{ left: `${pointer * 100}%` }}
          />
        )}
        {segments.map((s) => (
          <span
            key={s.zone}
            className="absolute bottom-0 h-2.5 rounded-[3px] transition-opacity duration-300"
            style={{
              left: `calc(${s.from * 100}% + 1px)`,
              width: `calc(${(s.to - s.from) * 100}% - 2px)`,
              background: ZONE_COLOURS[s.zone],
              opacity: s.zone === zone ? 1 : 0.28,
            }}
          />
        ))}
      </div>
      <div className="relative mt-0.5 h-3">
        {segments.map((s) => (
          <span
            key={s.zone}
            className="absolute -translate-x-1/2 font-timer text-[10px] font-semibold tabular-nums"
            style={{
              left: `${((s.from + s.to) / 2) * 100}%`,
              color: s.zone === zone ? "#fff" : "rgba(255,255,255,0.35)",
            }}
          >
            {s.zone}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function FreeTrainingGraph({ bpm, watts, hr, zone, subscribe, hrSource, basis = null }) {
  const [boxRef, boxH] = useBoxHeight();
  const contextFloors = useZoneFloors();
  const floors = basis ? zoneFloors(basis) : contextFloors;
  const max = basis?.max ?? null;

  const now = bpm != null ? Math.round(bpm) : null;
  const avg = hr ? averageHr(hr) : null;
  const top = hr?.max || null;
  const inZone = zone ? hr?.zoneSeconds?.[zone] || 0 : 0;
  const hasPower = watts != null;
  const pct = percentOfMax(now, max);
  const colour = zone ? ZONE_COLOURS[zone] : now != null ? "#fff" : "rgba(255,255,255,0.4)";

  // A tall box (portrait) gets the full layout; a short one (landscape)
  // drops the tiles into the hero row and shrinks the number.
  const compact = boxH > 0 && boxH < 300;
  const numberPx = Math.round(Math.max(48, Math.min(104, (boxH || 340) * (compact ? 0.3 : 0.27))));

  if (now == null && avg == null) {
    return (
      <div ref={boxRef} className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
        <Heart className="h-8 w-8 text-white/30" aria-hidden="true" />
        <p className="text-sm text-white/50">No heart rate connected</p>
      </div>
    );
  }

  const tiles = (
    <>
      <Tile label="Avg">{avg ?? "—"}</Tile>
      <Tile label="Max">{top ?? "—"}</Tile>
      {hasPower && (
        <Tile label="Power" colour="#facc15">
          {Math.round(watts)}
          <span className="ml-0.5 text-xs text-white/50">W</span>
        </Tile>
      )}
    </>
  );

  return (
    <div ref={boxRef} className="flex h-full w-full min-w-0 flex-col gap-2 px-1 py-1 text-left">
      {/* Zone name, and how long you've spent in it this session. */}
      <div className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]">
        <span className="flex min-w-0 items-center gap-1.5 truncate" style={{ color: colour }}>
          <Heart className="h-3.5 w-3.5 shrink-0 fill-current" aria-hidden="true" />
          {zone ? `Zone ${zone} · ${ZONE_LABELS[zone - 1]}` : floors ? "Below zones" : "Heart rate"}
        </span>
        {zone ? (
          <span className="shrink-0 font-timer normal-case tracking-normal text-white/50 tabular-nums">
            <b className="font-semibold text-white">{formatLapTime(inZone)}</b>
            {" in zone"}
          </span>
        ) : null}
      </div>

      {/* The number. */}
      <div className="flex items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <span
            className="font-timer font-bold leading-[0.9] tracking-tight tabular-nums transition-colors duration-300"
            style={{ fontSize: numberPx, color: colour }}
            aria-label={now != null ? `${now} beats per minute` : "No reading"}
          >
            {now ?? "—"}
          </span>
          <span className="mb-1 flex flex-col gap-0.5 leading-none">
            <span className="text-xs font-semibold tracking-[0.1em] text-white/50">BPM</span>
            {pct != null && (
              <span className="font-timer text-sm font-semibold tabular-nums text-white">
                {pct}%<span className="ml-1 font-sans text-[11px] font-medium text-white/40">of max</span>
              </span>
            )}
          </span>
        </div>
        {compact && <div className="flex shrink-0 gap-1.5">{tiles}</div>}
      </div>

      {floors && max ? <ZoneScaleBar floors={floors} max={max} bpm={now} zone={zone} /> : null}

      {/* The last ten minutes on the zone bands. */}
      <div className="flex min-h-[56px] flex-1 flex-col">
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg bg-white/[0.02]">
          <PowerChart
            subscribe={subscribe}
            hr={hrSource}
            hrBpm={bpm}
            windowSeconds={600}
            zoneBands
            className="absolute inset-0 block"
          />
        </div>
        <p className="mt-0.5 flex justify-between text-[10px] text-white/35">
          <span>{hasPower ? "−10 min · power and heart rate" : "−10 min"}</span>
          <span>now</span>
        </p>
      </div>

      {!compact && <div className={hasPower ? "grid grid-cols-3 gap-2" : "grid grid-cols-2 gap-2"}>{tiles}</div>}
    </div>
  );
}
